/**
 * useConquestEvents — detects newly-arrived `conquest` events in GameState
 * and returns them so the map can animate a territory-border flash.
 *
 * Contract:
 *   - The FIRST observed `events` array is treated as the "already seen"
 *     baseline so historical conquests don't replay on mount.
 *   - Subsequent state updates: any conquest event with `ts` greater than
 *     the highest ts seen so far is considered "new" and returned.
 *   - A Set of already-animated ts values is kept in a ref so reconnects
 *     and re-renders don't re-fire animations the player already saw.
 *
 * Returns an array of new ConquestEvent items since the last call. The
 * array is empty when there are no new events, or on first mount.
 */

import React from 'react'
import type { GameEvent } from '@warcarp/api-types'

/** The conquest variant extracted from the GameEvent discriminated union. */
type ConquestEvent = Extract<GameEvent, { kind: 'conquest' }>

/**
 * Diff prev vs. next `events` arrays and return new conquest events.
 *
 * "New" means the event's `ts` is strictly greater than `lastSeenTs`.
 * Exported as a pure function so it can be unit-tested independently
 * of React.
 */
export function detectNewConquests(
  nextEvents: GameEvent[] | undefined,
  lastSeenTs: number,
): ConquestEvent[] {
  if (!nextEvents || nextEvents.length === 0) return []
  return nextEvents.filter(
    (e): e is ConquestEvent => e.kind === 'conquest' && e.ts > lastSeenTs,
  )
}

export interface ConquestAnimationEntry {
  territoryId: string
  ownerColor: string
  /** epoch-ms when the animation was queued — used to clean up stale entries */
  startedAt: number
  /** Source event timestamp distinguishes conquests queued in the same batch. */
  eventTs?: number
}

export function conquestFlashKey(entry: ConquestAnimationEntry) {
  return `conquest-flash-${entry.territoryId}-${entry.eventTs ?? entry.startedAt}`
}

export function useConquestEvents(
  events: GameEvent[] | undefined,
  slotByPlayerId: Record<string, number>,
  playerColorHex: (slot: number) => string,
): ConquestAnimationEntry[] {
  // Track the highest ts we have already handed to animation. On first
  // render we set this to the maximum ts in the initial events array so
  // we don't replay historical conquests.
  const lastSeenTsRef = React.useRef<number | null>(null)
  // Guard against re-firing the same ts (e.g. React StrictMode double-
  // invoke, reconnects, or cache hits that re-deliver the same state).
  const animatedTsSet = React.useRef<Set<number>>(new Set())

  const [entries, setEntries] = React.useState<ConquestAnimationEntry[]>([])

  React.useEffect(() => {
    // If this is the first time we have events, set the baseline and
    // return without animating anything — we don't replay history.
    if (lastSeenTsRef.current === null) {
      const maxTs =
        events && events.length > 0
          ? Math.max(...events.map((e) => e.ts))
          : 0
      lastSeenTsRef.current = maxTs
      return
    }

    const newConquests = detectNewConquests(events, lastSeenTsRef.current)
    if (newConquests.length === 0) return

    // Advance the cursor to the highest ts we've seen.
    const maxNewTs = Math.max(...newConquests.map((e) => e.ts))
    lastSeenTsRef.current = Math.max(lastSeenTsRef.current, maxNewTs)

    // Filter out any that are already animating (e.g. strict-mode double fire).
    const fresh = newConquests.filter((e) => !animatedTsSet.current.has(e.ts))
    if (fresh.length === 0) return

    for (const e of fresh) animatedTsSet.current.add(e.ts)

    const now = Date.now()
    const newEntries: ConquestAnimationEntry[] = fresh.map((e) => {
      const slot = slotByPlayerId[e.attacker] ?? 1
      return {
        territoryId: e.territoryId,
        ownerColor: playerColorHex(slot),
        startedAt: now,
        eventTs: e.ts,
      }
    })

    // Animation total duration: 100ms hold + 800ms fade = 900ms.
    // We add a small buffer (200ms) before cleanup to let the fade finish.
    const ANIM_DURATION_MS = 1100

    setEntries((prev) => [...prev, ...newEntries])

    const handle = setTimeout(() => {
      const cutoff = now
      setEntries((prev) => prev.filter((e) => e.startedAt > cutoff))
    }, ANIM_DURATION_MS)

    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events])

  return entries
}
